import { t, tn } from '@app/i18n';
import type {
  Resource,
  ResourceCreate,
  ResourceTool,
  ResourceUpdate,
  Risco,
} from './Resources.api';

/** T7 — o que o drawer edita, defaults e conversão para o contrato (FR-029, FR-030). */

export type Method = NonNullable<ResourceTool['method']>;

/**
 * O item de `tools` como o contrato o declara. A anotacao e o que impede o
 * proximo renomeio de passar: sem ela o retorno e inferido, o literal deixa de
 * ser fresco no ponto de uso e o `tsc` cala sobre chave errada — foi assim que
 * `apim_name`/`metodo`/`descricao` sobreviveram ao fio EN.
 */
type ToolInput = NonNullable<ResourceUpdate['tools']>[number];

export const METHODS: Method[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/** `''` = risco não declarado — o vocabulário do FR-032 não tem "desconhecido" como valor. */
export type RiskForm = Risco | '';

export interface ToolFormValues {
  /** `null` = linha nova (só manuais e MCP por policy criam tools). */
  id: string | null;
  /**
   * Nome como o gateway expõe, que é o nome que o PDP vê (D2 do aim-cpj): somente
   * leitura para importadas.
   */
  apim_name: string;
  metodo: Method | null;
  path: string;
  descricao: string;
  risk: RiskForm;
  /** Parâmetros que aceitam limite — chips, vazio = "não aceita limite". */
  constrainable: string[];
  /**
   * O Display name que o gateway mostra ao lado da tool (aim-uig.40, decisão D3). Só
   * leitura: o dono é o gateway, e `ToolInput` nem tem o campo — o backend o mantém
   * numa edição. `null` = o gateway não tem, ou a linha é nova.
   */
  display_name: string | null;
}

export interface ResourceFormValues {
  apim_id: string;
  nome: string;
  // Só `mcp` ou `api`: o formulário cria e edita servidores de tools. O recurso agente (fase 2) nasce da leitura
  // do gateway e não passa por aqui.
  tipo: ResourceCreate['kind'];
  endpoint: string;
  owner: string;
  environment: string;
  descricao: string;
  tools: ToolFormValues[];
  /**
   * Site onde o recurso é exposto (P1, marco C). **Vazio = o `default`**.
   *
   * String vazia, e não `null`, porque é o que um combobox limpo devolve — e o
   * `forCreation` a converte em campo ausente, que é como o contrato escreve «use o
   * padrão». Um `null` no meio obrigaria a tela a distinguir dois jeitos de dizer a
   * mesma coisa.
   */
  site_id: string;
}

export function toolDefaults(tool?: ResourceTool): ToolFormValues {
  return {
    id: tool?.id ?? null,
    apim_name: tool?.gateway_name ?? '',
    metodo: tool?.method ?? null,
    path: tool?.path ?? '',
    descricao: tool?.description ?? '',
    risk: tool?.risk ?? '',
    constrainable: tool?.constrainable ?? [],
    display_name: tool?.display_name ?? null,
  };
}

export function resourceDefaults(resource?: Resource): ResourceFormValues {
  return {
    apim_id: resource?.gateway_id ?? '',
    nome: resource?.name ?? '',
    tipo: resource && resource.kind !== 'agent' ? resource.kind : 'mcp',
    endpoint: resource?.endpoint ?? '',
    owner: resource?.owner ?? '',
    environment: resource?.environment ?? '',
    descricao: resource?.description ?? '',
    // O site vem preenchido na EDIÇÃO só para a tela poder mostrá-lo; o campo é
    // somente leitura ali (recurso não muda de site, como o slug não muda).
    site_id: resource?.site_id ?? '',
    tools: (resource?.tools ?? []).map(toolDefaults),
  };
}

const texto = (valor: string) => valor.trim();
const textOrNull = (valor: string) => texto(valor) || null;

function toolForContract(tool: ToolFormValues, manual: boolean): ToolInput {
  return {
    ...(tool.id ? { id: tool.id } : {}),
    gateway_name: texto(tool.apim_name),
    method: tool.metodo,
    path: textOrNull(tool.path),
    description: textOrNull(tool.descricao),
    risk: tool.risk === '' ? null : tool.risk,
    // R22: tool de resource manual nunca aceita limite — o PDP não impõe nada
    // sobre ele, e mandar `constrainable` daria a impressão contrária (FR-034).
    constrainable: manual ? [] : tool.constrainable,
  };
}

/** Corpo de `createResource` — só resources manuais nascem por aqui (R22). */
export function forCreation(valores: ResourceFormValues): ResourceCreate {
  return {
    gateway_id: texto(valores.apim_id),
    name: textOrNull(valores.nome) ?? texto(valores.apim_id),
    kind: valores.tipo,
    endpoint: texto(valores.endpoint),
    owner: textOrNull(valores.owner),
    environment: textOrNull(valores.environment),
    description: textOrNull(valores.descricao),
    // Ausente quando vazio, e não `site_id: null`: o contrato trata os dois como «o
    // padrão», e mandar o campo com nulo faria a requisição de todo tenant
    // single-site carregar uma decisão que ninguém tomou.
    ...(texto(valores.site_id) ? { site_id: texto(valores.site_id) } : {}),
    tools: valores.tools.map((tool) => toolForContract(tool, true)),
  };
}

/**
 * Corpo de `updateResource`. `tools` é a **lista completa** depois da edição:
 * tool em uso ausente daqui volta 409 `tool_em_uso` — remover se faz pela ação
 * de excluir tool, que passa pela confirmação de impacto (FR-035).
 */
export function forAtualizacao(valores: ResourceFormValues, manual: boolean): ResourceUpdate {
  return {
    name: textOrNull(valores.nome) ?? texto(valores.apim_id),
    endpoint: textOrNull(valores.endpoint),
    owner: textOrNull(valores.owner),
    environment: textOrNull(valores.environment),
    description: textOrNull(valores.descricao),
    tools: valores.tools.map((tool) => toolForContract(tool, manual)),
  };
}

// `donoDoUsuario` mudou-se para `@app/shared/dono` em 28/08, quando a identidade
// (T5) ganhou dono e passou a precisar da mesma regra. Reexportado aqui por
// compatibilidade de importação; a regra é lá.
export { userOwner } from '@app/shared/owner';

/**
 * O resource é governado pela policy global do gateway (aim-uig.40 §5.1): o gateway
 * manda ao PDP o nome da tool como o vê, então o nome no PDP não se edita ali. A regra
 * (`api/<…>` ou `mcp/<…>`) é do backend, que a publica derivada — a tela não guarda uma
 * terceira cópia dela.
 */
export function governedIs(resource?: Resource): boolean {
  return resource?.governed_by_global_policy === true;
}

/** `manual` = fora do gateway: local, sem enforcement no PDP (FR-034). */
export function manualIs(resource?: Resource): boolean {
  return resource?.origin === 'manual' || resource?.out_of_gateway === true;
}

/** Origem, somente leitura na aba Geral (FR-029). */
export function textOrigin(resource?: Resource): string {
  if (!resource || resource.origin === 'manual') return t('resources.form.origin.manual');
  return resource.items_origin
    ? t('resources.form.origin.apimWithItems', { items: resource.items_origin })
    : t('resources.form.origin.apim');
}

/**
 * Confirmação de exclusão (FR-035, FR-130) — a frase literal do PRD T7.
 * "Remover uma tool de um recurso segue a mesma regra", então só o sujeito muda.
 */
export function textImpact(
  alvo: 'resource' | 'tool',
  capabilities: number,
  contratos: number,
  politicas: number,
): string {
  // As três contagens concordam com o número: era «1 capabilities de 1
  // contratos» antes do plural de duas formas (glossário da auditoria de copy).
  return t('resources.impact.text', {
    target: t(`resources.impact.target.${alvo}`),
    capabilities: tn('resources.impact.capabilities', capabilities),
    contracts: tn('resources.impact.contracts', contratos),
    policies: tn('resources.impact.policies', politicas),
  });
}
